import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OrgNodeService } from './org-node.service';

export interface TimezoneOption {
  timezone: string;
  label: string;       // "Asia/Colombo (UTC+5:30)"
  companies: string[]; // company names that use this timezone
}

const UTC_OFFSETS: Record<string, string> = {
  'UTC':                  'UTC+0',
  'Europe/London':        'UTC+0/+1',
  'Asia/Colombo':         'UTC+5:30',
  'Asia/Kolkata':         'UTC+5:30',
  'Asia/Kuala_Lumpur':    'UTC+8',
  'America/Toronto':      'UTC-5/-4',
  'America/New_York':     'UTC-5/-4',
  'Asia/Dubai':           'UTC+4',
  'Asia/Singapore':       'UTC+8',
  'Australia/Sydney':     'UTC+10/+11',
};

@Injectable({ providedIn: 'root' })
export class OrgTimezoneService {
  private readonly http = inject(HttpClient);
  private readonly orgNodeService = inject(OrgNodeService);

  private _options    = signal<TimezoneOption[]>([]);
  private _defaultTz  = signal<string>('UTC');
  private _loaded     = false;

  readonly options   = this._options.asReadonly();
  readonly defaultTz = this._defaultTz.asReadonly();

  load(): void {
    if (this._loaded) return;
    this._loaded = true;

    this.orgNodeService.getNodeMaps().subscribe({
      next: maps => {
        const map = new Map<string, string[]>();

        for (const [code, info] of maps.infoMap.entries()) {
          const tz: string = info.timezone ?? 'UTC';
          if (!map.has(tz)) map.set(tz, []);
          map.get(tz)!.push(info.name);
        }

        const options: TimezoneOption[] = Array.from(map.entries()).map(([tz, companies]) => ({
          timezone: tz,
          label:    `${tz} (${UTC_OFFSETS[tz] ?? 'UTC'})`,
          companies,
        }));

        this._options.set(options);

        // Default: first non-UTC company timezone, fallback UTC
        const nonUtc = options.find(o => o.timezone !== 'UTC');
        this._defaultTz.set(nonUtc?.timezone ?? 'UTC');
      },
      error: () => {
        this._options.set([{ timezone: 'UTC', label: 'UTC (UTC+0)', companies: [] }]);
        this._defaultTz.set('UTC');
      }
    });
  }
}
